import { type FormEvent, useEffect, useMemo, useState } from 'react'
import { Download, MapPin, Plus, RefreshCw, Trees } from 'lucide-react'
import { Modal } from '../components/Modal'
import { StatusPill } from '../components/StatusPill'
import { api } from '../lib/api'
import { number, percent } from '../lib/format'

type CreateMode = 'campaign' | 'site' | 'plot'

export function CampaignsLive() {
  const [campaigns, setCampaigns] = useState<any[]>([])
  const [sites, setSites] = useState<any[]>([])
  const [plots, setPlots] = useState<any[]>([])
  const [mode, setMode] = useState<CreateMode | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [modalError, setModalError] = useState('')
  const [busy, setBusy] = useState(false)

  const reload = async () => {
    setLoading(true)
    try {
      const [campaignRows, siteRows, plotRows] = await Promise.all([api.campaigns(), api.sites(), api.plots()])
      setCampaigns(campaignRows)
      setSites(siteRows)
      setPlots(plotRows)
      setError('')
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void reload() }, [])

  const active = useMemo(() => campaigns.filter((campaign) => campaign.status === 'active'), [campaigns])
  const campaignName = (id?: string | null) => campaigns.find((campaign) => campaign.id === id)?.name

  async function submitCampaign(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setBusy(true)
    setModalError('')
    const form = new FormData(event.currentTarget)
    try {
      await api.createCampaign({
        name: form.get('name'),
        description: form.get('description') || undefined,
        startsOn: form.get('startsOn') || undefined,
        targetTrees: Number(form.get('targetTrees') || 0),
        status: 'active',
        publicVisible: true,
      })
      setMode(null)
      await reload()
    } catch (err) {
      setModalError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  async function submitSite(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setBusy(true)
    setModalError('')
    const form = new FormData(event.currentTarget)
    try {
      const response = await fetch('/api/sites', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          campaignId: form.get('campaignId') || null,
          name: form.get('name'),
          locality: form.get('locality') || undefined,
          district: form.get('district') || undefined,
          region: 'Jammu and Kashmir',
          locationPrivacy: 'approximate',
        }),
      })
      if (!response.ok) {
        const result = await response.json()
        throw new Error(result.error || 'Could not create site')
      }
      setMode(null)
      await reload()
    } catch (err) {
      setModalError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  async function submitPlot(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setBusy(true)
    setModalError('')
    const form = new FormData(event.currentTarget)
    try {
      const response = await fetch('/api/plots', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          siteId: form.get('siteId'),
          campaignId: form.get('campaignId') || null,
          name: form.get('name'),
          code: form.get('code'),
          monitoringMode: form.get('monitoringMode'),
          targetCount: Number(form.get('targetCount') || 0),
        }),
      })
      if (!response.ok) {
        const result = await response.json()
        throw new Error(result.error || 'Could not create plot')
      }
      setMode(null)
      await reload()
    } catch (err) {
      setModalError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  const modalLabel = mode === 'campaign' ? 'Create campaign' : mode === 'site' ? 'Add site' : 'Add monitoring plot'

  return (
    <div className="page-stack">
      <section className="page-header-row">
        <div>
          <h2>Campaigns, sites and plots</h2>
        </div>
        <div className="hero-actions">
          <button className="button button--primary" onClick={() => { setModalError(''); setMode('campaign') }}><Plus size={17} /> Campaign</button>
          <button className="button button--secondary" onClick={() => { setModalError(''); setMode('site') }}><MapPin size={17} /> Site</button>
          <button className="button button--secondary" onClick={() => { setModalError(''); setMode('plot') }}><Trees size={17} /> Plot</button>
          <button className="button button--secondary" onClick={() => void reload()} disabled={loading}><RefreshCw size={16} /> Refresh</button>
        </div>
      </section>

      {error && <section className="panel" role="alert"><div className="panel-heading"><div><h3>Could not load programme records</h3></div><button className="button button--secondary" onClick={() => void reload()} disabled={loading}>Try again</button></div><p style={{ padding: '0 19px 17px', margin: 0 }}>{error}</p></section>}

      <section className="panel">
        <div className="panel-heading"><div><h3>{loading ? 'Loading campaigns…' : error ? '—' : `${active.length} active · ${campaigns.length} total`}</h3></div><Trees size={20} /></div>
        {loading ? <div className="empty-state" role="status"><span>Loading campaigns…</span></div> : campaigns.length === 0 ? <div className="empty-state"><Trees size={30} /><strong>No campaigns yet</strong><span>Create a campaign to organize sites and plots.</span></div> : <div className="campaign-list">{campaigns.map((campaign) => {
          const planted = Number(campaign.planted || 0)
          const alive = Number(campaign.verified_alive || 0)
          const assessed = Number(campaign.assessed || 0)
          return <article className="campaign-row" key={campaign.id}>
            <div className="campaign-row__heading">
              <div><strong>{campaign.name}</strong><span>{number.format(planted)} planted · target {number.format(Number(campaign.target_trees || 0))}</span></div>
              <div className="hero-actions"><a className="button button--secondary button--small" href={`/api/export/campaigns/${encodeURIComponent(campaign.id)}.csv`}><Download size={14} /> CSV</a><StatusPill value={campaign.status} compact /></div>
            </div>
            <div className="dual-stat"><span><strong>{percent(alive, planted)}%</strong> verified alive</span><span><strong>{percent(assessed, planted)}%</strong> assessed</span><span><strong>{number.format(Number(campaign.dead || 0))}</strong> dead</span></div>
          </article>
        })}</div>}
      </section>

      <div className="dashboard-grid">
        <section className="panel">
          <div className="panel-heading"><div><h3>{loading ? 'Loading sites…' : error ? '—' : `${sites.length} plantation locations`}</h3></div><MapPin size={20} /></div>
          {loading ? <div className="empty-state" role="status"><span>Loading sites…</span></div> : sites.length === 0 ? <div className="empty-state"><MapPin size={28} /><strong>No sites yet</strong><span>Add a site to place field work.</span></div> : <div className="campaign-list">{sites.map((site) => <article className="campaign-row" key={site.id}><div className="campaign-row__heading"><div><strong>{site.name}</strong><span>{[site.locality, site.district, site.region].filter(Boolean).join(', ') || 'Location pending'}</span></div><StatusPill value={site.location_privacy || 'approximate'} compact /></div><div className="campaign-row__footer"><span>{campaignName(site.campaign_id) || 'Independent site'}</span><span>Location privacy: {site.location_privacy || 'approximate'}</span></div></article>)}</div>}
        </section>

        <section className="panel">
          <div className="panel-heading"><div><h3>{loading ? 'Loading plots…' : error ? '—' : `${plots.length} monitoring units`}</h3></div><Trees size={20} /></div>
          {loading ? <div className="empty-state" role="status"><span>Loading plots…</span></div> : plots.length === 0 ? <div className="empty-state"><Trees size={28} /><strong>No plots yet</strong><span>Add a plot when sample monitoring is needed.</span></div> : <div className="campaign-list">{plots.map((plot) => <article className="campaign-row" key={plot.id}><div className="campaign-row__heading"><div><strong>{plot.name}</strong><span>{plot.code} · {plot.site_name}</span></div><StatusPill value={plot.monitoring_mode} compact /></div><div className="dual-stat"><span><strong>{number.format(Number(plot.target_count || 0))}</strong> target trees</span><span>{campaignName(plot.campaign_id) || 'Site campaign'}</span></div></article>)}</div>}
        </section>
      </div>

      {mode && <Modal label={modalLabel} onClose={() => { if (!busy) setMode(null) }}>
        <h2>{modalLabel}</h2>
        {modalError && <div className="inline-alert inline-alert--danger" role="alert"><div><strong>Could not save this record</strong><span>{modalError}</span></div></div>}
        {mode === 'campaign' ? <form onSubmit={submitCampaign}>
          <label className="field-label"><span>Name</span><input name="name" required /></label>
          <label className="field-label"><span>Description <em>optional</em></span><textarea name="description" rows={3} /></label>
          <label className="field-label"><span>Start date <em>optional</em></span><input name="startsOn" type="date" /></label>
          <label className="field-label"><span>Target trees</span><input name="targetTrees" type="number" min="0" defaultValue="1000" /></label>
          <button disabled={busy} className="button button--primary button--wide">{busy ? 'Creating…' : 'Create campaign'}</button>
        </form> : mode === 'site' ? <form onSubmit={submitSite}>
          <label className="field-label"><span>Campaign <em>optional</em></span><select name="campaignId"><option value="">Independent / assign later</option>{campaigns.map((campaign) => <option value={campaign.id} key={campaign.id}>{campaign.name}</option>)}</select></label>
          <label className="field-label"><span>Site name</span><input name="name" required /></label>
          <label className="field-label"><span>Locality <em>optional</em></span><input name="locality" /></label>
          <label className="field-label"><span>District <em>optional</em></span><input name="district" /></label>
          <button disabled={busy} className="button button--primary button--wide">{busy ? 'Adding…' : 'Add site'}</button>
        </form> : <form onSubmit={submitPlot}>
          <label className="field-label"><span>Site</span><select name="siteId" required><option value="">Choose site</option>{sites.map((site) => <option value={site.id} key={site.id}>{site.name}</option>)}</select></label>
          <label className="field-label"><span>Campaign <em>optional</em></span><select name="campaignId"><option value="">Inherit / assign later</option>{campaigns.map((campaign) => <option value={campaign.id} key={campaign.id}>{campaign.name}</option>)}</select></label>
          <label className="field-label"><span>Name</span><input name="name" required /></label>
          <label className="field-label"><span>Code</span><input name="code" required placeholder="HV-12" /></label>
          <label className="field-label"><span>Monitoring mode</span><select name="monitoringMode"><option value="individual">Individual trees</option><option value="plot">Plot sample</option><option value="hybrid">Hybrid</option></select></label>
          <label className="field-label"><span>Target count</span><input name="targetCount" type="number" min="0" defaultValue="500" /></label>
          <button disabled={busy} className="button button--primary button--wide">{busy ? 'Adding…' : 'Add plot'}</button>
        </form>}
        <button type="button" className="button button--secondary button--wide" onClick={() => setMode(null)} disabled={busy}>Cancel</button>
      </Modal>}
    </div>
  )
}
